import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Testimonial, CreateTestimonialData, TestimonialsQueryParams, ApiError, PaginatedResponse, Language } from '../../types/api';

interface UseTestimonialsState {
  testimonials: Testimonial[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

interface UseTestimonialsReturn extends UseTestimonialsState {
  fetchTestimonials: (params?: TestimonialsQueryParams) => Promise<void>;
  createTestimonial: (data: CreateTestimonialData) => Promise<Testimonial | null>;
  deleteTestimonial: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export const useTestimonials = (
  initialParams?: TestimonialsQueryParams,
  autoFetch: boolean = true,
  language: Language = 'pt'
): UseTestimonialsReturn => {
  const [state, setState] = useState<UseTestimonialsState>({
    testimonials: [],
    loading: false,
    error: null,
    pagination: null,
  });

  const [queryParams, setQueryParams] = useState<TestimonialsQueryParams>(
    initialParams || { page: 1, limit: 10 }
  );

  const fetchTestimonials = useCallback(async (params?: TestimonialsQueryParams) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Caller can override the target language via params.target; that wins
      // over the hook's closure (useful when reacting to i18n.languageChanged
      // directly from a parent component, where the hook's closure may be
      // one render behind).
      const merged = (params || queryParams) as TestimonialsQueryParams & { target?: Language };
      const effectiveTarget = (merged.target || language) as Language;
      api.setLanguage(effectiveTarget);
      const response: PaginatedResponse<Testimonial> = await api.testimonials.getAll({
        ...merged,
        target: effectiveTarget,
      } as TestimonialsQueryParams & { target: Language });
      setState({
        testimonials: response.data,
        loading: false,
        error: null,
        pagination: response.pagination,
      });
      if (params) setQueryParams(params);
    } catch (error: any) {
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [language, queryParams]);

  const createTestimonial = useCallback(
    async (data: CreateTestimonialData): Promise<Testimonial | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const newTestimonial = await api.testimonials.create(data);
        setState((prev) => ({ ...prev, loading: false }));
        return newTestimonial;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error as ApiError,
        }));
        return null;
      }
    },
    []
  );

  const deleteTestimonial = useCallback(async (id: string): Promise<boolean> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await api.testimonials.delete(id);
      setState((prev) => ({
        ...prev,
        testimonials: prev.testimonials.filter((t) => t.id !== id),
        loading: false,
      }));
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
      return false;
    }
  }, []);

  const refresh = useCallback(() => {
    return fetchTestimonials(queryParams);
  }, [fetchTestimonials, queryParams]);

  useEffect(() => {
    if (autoFetch) {
      fetchTestimonials();
    }
  }, [autoFetch, fetchTestimonials]);

  return {
    ...state,
    fetchTestimonials,
    createTestimonial,
    deleteTestimonial,
    refresh,
  };
};
